import { createApi } from "@reduxjs/toolkit/query/react";
import { baseQuery } from "@/utils/BaseQuery";
import { ApiResponse, PaginatedApiResponse } from "@/types/api.types";

export interface HarborAssociationRequest {
  id: string;
  harbor_id: string;
  club_id: string;
  status:
    | "pending"
    | "approved"
    | "rejected"
    | "revoked"
    | "approved_by_admin"
    | "club_approval_pending";
  request_message: string | null;
  response_message: string | null;
  created_at: string;
  updated_at: string;
  harbor: {
    id: string;
    name: string;
    description: string;
    location: string;
    logo: string;
  };
  club: {
    id: string;
    name: string;
    abbreviation: string;
    logo: string;
  };
}

// Harbor Club Request interfaces
export interface HarborClubRequest {
  id: string;
  club_id: string;
  harbor_id: string;
  status:
    | "pending"
    | "approved"
    | "rejected"
    | "revoked"
    | "approved_by_admin"
    | "club_approval_pending";
  request_message: string | null;
  response_message: string | null;
  created_at: string;
  updated_at: string;
  harbor: {
    id: string;
    name: string;
    description: string;
    location: string;
    logo: string;
  };
  club: {
    id: string;
    name: string;
    abbreviation: string;
    logo: string;
  };
}

export interface HarborAssociationParams {
  page?: number;
  limit?: number;
  search?: string;
  status?: string;
  pending_only?: boolean;
  club_id?: string;
}

export interface HarborClubRequestParams {
  page?: number;
  limit?: number;
  search?: string;
  status?: string;
  harbor_id?: string;
}

export interface HarborClubRequestActionParams {
  request_id: string;
  response_message?: string;
}

export interface CancelAssociationRequestParams {
  request_id: string;
  action: "withdraw" | "reject";
  reason?: string;
}

export interface RevokeAssociationParams {
  request_id: string;
  reason?: string;
}

export interface CreateHarborAssociationRequest {
  harbor_ids: string[];
  request_message?: string;
  club_id?: string;
}

export const harborAssociationApi = createApi({
  reducerPath: "harborAssociationApi",
  baseQuery,
  tagTypes: ["HarborAssociations"],
  endpoints: (builder) => ({
    getHarborAssociations: builder.query<
      PaginatedApiResponse<HarborAssociationRequest[]>,
      HarborAssociationParams
    >({
      query: (params) => ({
        url: "harbor-association/requests/club",
        method: "GET",
        params: params || undefined,
      }),
      forceRefetch: ({ currentArg, previousArg }) => {
        if (!previousArg) return true;
        if (currentArg?.page !== previousArg?.page) return true;
        if (currentArg?.limit !== previousArg?.limit) return true;
        if (currentArg?.search !== previousArg?.search) return true;
        if (currentArg?.status !== previousArg?.status) return true;
        return false;
      },
      providesTags: (result) =>
        result?.data
          ? [
              ...result.data.map((request) => ({
                type: "HarborAssociations" as const,
                id: request.id,
              })),
              { type: "HarborAssociations", id: "LIST" },
            ]
          : [{ type: "HarborAssociations", id: "LIST" }],
    }),

    createHarborAssociations: builder.mutation<
      ApiResponse<void>,
      CreateHarborAssociationRequest
    >({
      query: (data) => ({
        url: "harbor-association/request-multiple",
        method: "POST",
        data,
      }),
      invalidatesTags: [{ type: "HarborAssociations", id: "LIST" }],
    }),

    cancelHarborAssociation: builder.mutation<
      ApiResponse<void>,
      CancelAssociationRequestParams
    >({
      query: ({ request_id, action, reason }) => ({
        url: `harbor-association/requests/${request_id}/cancel`,
        method: "PUT",
        data: {
          action,
          reason,
        },
      }),
      invalidatesTags: (result, error, { request_id }) => [
        { type: "HarborAssociations", id: request_id },
        { type: "HarborAssociations", id: "LIST" },
      ],
    }),

    getHarborAssociationById: builder.query<HarborAssociationRequest, string>({
      query: (id) => ({
        url: `harbor-association/request/${id}`,
        method: "GET",
      }),
      providesTags: (result, error, id) => [{ type: "HarborAssociations", id }],
    }),

    getHarborClubRequests: builder.query<
      PaginatedApiResponse<HarborClubRequest[]>,
      HarborClubRequestParams
    >({
      query: (params) => ({
        url: "harbor-association/requests/all",
        method: "GET",
        params: params || undefined,
      }),
      forceRefetch: ({ currentArg, previousArg }) => {
        if (!previousArg) return true;
        if (currentArg?.page !== previousArg?.page) return true;
        if (currentArg?.limit !== previousArg?.limit) return true;
        if (currentArg?.search !== previousArg?.search) return true;
        if (currentArg?.status !== previousArg?.status) return true;
        if (currentArg?.harbor_id !== previousArg?.harbor_id) return true;
        return false;
      },
      providesTags: (result) =>
        result?.data
          ? [
              ...result.data.map((request) => ({
                type: "HarborAssociations" as const,
                id: request.id,
              })),
              { type: "HarborAssociations", id: "LIST" },
            ]
          : [{ type: "HarborAssociations", id: "LIST" }],
    }),

    getHarborClubRequestById: builder.query<HarborClubRequest, string>({
      query: (id) => ({
        url: `harbor-association/request/${id}`,
        method: "GET",
      }),
      providesTags: (result, error, id) => [{ type: "HarborAssociations", id }],
    }),

    approveHarborClubRequest: builder.mutation<
      ApiResponse<void>,
      HarborClubRequestActionParams
    >({
      query: ({ request_id, response_message }) => ({
        url: `harbor-association/requests/${request_id}/respond`,
        method: "PUT",
        data: {
          status: "approved",
          response_message,
        },
      }),
      invalidatesTags: (result, error, { request_id }) => [
        { type: "HarborAssociations", id: request_id },
        { type: "HarborAssociations", id: "LIST" },
      ],
    }),

    rejectHarborClubRequest: builder.mutation<
      ApiResponse<void>,
      HarborClubRequestActionParams
    >({
      query: ({ request_id, response_message }) => ({
        url: `harbor-association/requests/${request_id}/respond`,
        method: "PUT",
        data: {
          status: "rejected",
          response_message,
        },
      }),
      invalidatesTags: (result, error, { request_id }) => [
        { type: "HarborAssociations", id: request_id },
        { type: "HarborAssociations", id: "LIST" },
      ],
    }),

    revokeHarborClubRequest: builder.mutation<
      ApiResponse<void>,
      RevokeAssociationParams
    >({
      query: ({ request_id, reason }) => ({
        url: `harbor-association/requests/${request_id}/revoke`,
        method: "PUT",
        data: {
          reason,
        },
      }),
      invalidatesTags: (result, error, { request_id }) => [
        { type: "HarborAssociations", id: request_id },
        { type: "HarborAssociations", id: "LIST" },
      ],
    }),
  }),
});

export const {
  useGetHarborAssociationsQuery,
  useCreateHarborAssociationsMutation,
  useCancelHarborAssociationMutation,
  useGetHarborAssociationByIdQuery,
  useGetHarborClubRequestsQuery,
  useGetHarborClubRequestByIdQuery,
  useApproveHarborClubRequestMutation,
  useRejectHarborClubRequestMutation,
  useRevokeHarborClubRequestMutation,
} = harborAssociationApi;
